import React, { useRef, useState, useEffect, useTransition } from "react";
import "react-chat-elements/dist/main.css";
import { MessageBox, ChatList } from "react-chat-elements";
import {
  Col,
  Flex,
  Row,
  Input,
  Button,
  Layout,
  Typography,
  Form,
  Popover,
  Avatar,
  Tooltip,
  Upload,
  Modal,
} from "antd";
import {
  SendOutlined,
  SearchOutlined,
  SmileOutlined,
  PaperClipOutlined,
  FileImageOutlined,
  FolderOutlined,
} from "@ant-design/icons";
import EmojiPicker from "./EmojiPicker";
import { colors } from "../../../constants/colors";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { appConfig } from "../../../constants/config/appConfig";
import { appAssetsConfig } from "../../../constants/config/appAssetsConfig";
import Spinner from "../spinner/Spinner";
import { LeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserOutlined } from "@ant-design/icons";
import "./ChatComponent.scss";
import { FallbackChatUI } from "../../../assets/svg";
import {
  checkIfFileTypeIsValidForDocumentAttachment,
  checkIfFileTypeIsValidForImageAttachment,
  downloadUrl,
  formulateAttachmentPreview,
  readImageFileAsync,
} from "../../../utils/utils";
import { userRoles } from "../../../constants";
import { toastService } from "../toast-messages/toast";

const ChatComponent = ({
  chatList,
  isChatList = true,
  chatMessages,
  onChatClicked = () => {},
  onSubmit,
  selectedUserDetails,
  disabledChat = false,
  isChat,
  requestMessage,
  onRequestStatusChanged,
  backButtonDetails = {
    isBackButton: false,
    navigate: () => {},
  },
  setSearchValue,
  searchLoading,
}) => {
  const { userSession } = useLocalStorage(appConfig.SESSION_USER_INFO);
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [messageType] = useState("TEXT");
  const inputRef = useRef(null);
  const [, setOpenEmojiPickerDialog] = useState(false);
  const [loading] = useState(false);
  const [requestLoader, setRequestLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  /* module dependencies for attachment */
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [attachmentList, setAttachmentList] = useState([]);
  /* module dependencies for attachment */

  const messageContainerRef = useRef(null);
  /* module dependencies for attachment - single */
  const [, setSingleAttachmentObj] = useState(null);
  const imageRef = useRef(null);
  const documentRef = useRef(null);

  /* module dependencies for attachment - single */

  /* on Image changed- single attachment */
  const onImageChanged = (e) => {
    const file = e.target.files[0];

    const attachmentFileName = file.name;

    const fileTypeIsValidRes = checkIfFileTypeIsValidForImageAttachment(attachmentFileName);

    if (fileTypeIsValidRes.isValid === false) {
      toastService.error(fileTypeIsValidRes.isInvalidMessage);
    } else {
      setSingleAttachmentObj(file);
      if (file) {
        const dataToBeSent = {
          message_type: "IMAGE",
          images: file,
        };

        onSubmit(dataToBeSent);
        e.target.value = "";
      }
    }
  };
  /* on Image changed- single attachment */

  /* on Image changed- single attachment */
  const onDocumentChanged = (e) => {
    const file = e.target.files[0];

    const attachmentFileName = file.name;

    const fileTypeIsValidRes = checkIfFileTypeIsValidForDocumentAttachment(attachmentFileName);

    if (fileTypeIsValidRes.isValid === false) {
      toastService.error(fileTypeIsValidRes.isInvalidMessage);
    } else {
      if (file) {
        const dataToBeSent = {
          message_type: "DOCUMENT",
          documents: file,
        };
        onSubmit(dataToBeSent);
        e.target.value = "";
      }
    }
  };
  /* on Image changed- single attachment */

  /* open image preview for dialog - multiple attachment */
  const handleAttachmentPreview = async (file) => {
    const formulateAttachment = await formulateAttachmentPreview(
      file,
      file?.name,
      file.url
    );
    if (!formulateAttachment?.isFileTypeImage) {
      downloadUrl(formulateAttachment?.fileImagePreview);
    } else {
      if (!file.url && !file.preview) {
        file.preview = await readImageFileAsync(file.originFileObj);
      }
      setPreviewImageSrc(file?.url || file?.preview);
      setOpenPreviewDialog(true);
      const tempPreviewTitle =
        file?.name || file?.url?.substring(file?.url?.lastIndexOf("/") + 1);
      setPreviewTitle(tempPreviewTitle);
    }
  };
  /* open image preview for dialog - multiple attachment */

  useEffect(() => {
    scrollToBottomOnMount();
  }, [chatMessages]);

  const scrollToBottomOnMount = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  };

  /* on send message */
  const onSendMessage = (values) => {
    if (
      messageType === "TEXT" &&
      (values?.message === "" ||
        values?.message === undefined ||
        values?.message === null)
    ) {
      return;
    }
    const dataToBeSent = {
      message_type: messageType,
      message: values?.message,
    };
    onSubmit(dataToBeSent);
    form.resetFields();
    setOpenEmojiPickerDialog(false);
    // onChatClicked(selectedUserDetails, setRequestLoader, messageContainerRef);
  };
  /* on send message */

  const messageTypes = {
    IMAGE: "photo",
    TEXT: "text",
    DOCUMENT: "file",
    ORDER_PAYMENT_LINK: "text",
  };

  const getMessagePosition = (element, type) => {
    switch (type) {
      case "message":
        if (
          !element?.customer_id ||
          element?.customer_id === "" ||
          element?.customer_id === null ||
          element?.customer_id === undefined
        ) {
          if (
            element?.from_id === userSession.userId ||
            element?.from_id?._id === userSession.userId
          ) {
            return "right";
          } else {
            return "left";
          }
        } else {
          if (element?.from_id._id !== element?.customer_id) {
            return "right";
          } else {
            return "left";
          }
        }

      case "backgroundColor":
        if (
          !element?.customer_id ||
          element?.customer_id === "" ||
          element?.customer_id === null ||
          element?.customer_id === undefined
        ) {
          if (
            element?.from_id === userSession.userId ||
            element?.from_id?._id === userSession.userId
          ) {
            return colors.greenishBlue;
          } else {
            return colors.white;
          }
        } else {
          if (element?.from_id._id !== element?.customer_id) {
            return colors.greenishBlue;
          } else {
            return colors.white;
          }
        }

      default:
        break;
    }
  };

  return (
    <Row className="chatContainer">
      {isChatList ? (
        <Col span={7} style={{ background: colors.white }}>
          <Layout.Header
            style={{
              background: colors.white,
              borderBottom: `2px solid ${colors.lightGray}`,
            }}
          >
            <Input
              size="large"
              prefix={<SearchOutlined color="secondary" />}
              placeholder={t("CommonUtils.search")}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Layout.Header>
          <Typography.Title style={{ padding: 10 }} type="secondary" level={2}>
            {t("CommonUtils.messages")}
          </Typography.Title>
          {searchLoading ? (
            <Spinner />
          ) : (
            <ChatList
              className="chatList"
              dataSource={chatList.map((element) => ({
                ...element,
                direction: "rtl",
              }))}
              onClick={(element) => {
                onChatClicked(element, setRequestLoader, messageContainerRef);
                if (!isChat) setOpen(true);
              }}
              onAvatarError={() => appAssetsConfig.DEFAULT_PROFILE_IMAGE}
              style={{ direction: "rtl" }}
            />
          )}
        </Col>
      ) : (
        <></>
      )}
      <Col span={isChatList ? 17 : 24}>
        {isChatList &&
        !open &&
        (!selectedUserDetails ||
          selectedUserDetails === null ||
          selectedUserDetails === undefined ||
          Object.keys(selectedUserDetails)?.length === 0) ? (
          <Flex
            align="center"
            justify="center"
            style={{ margin: "auto", height: "100%" }}
          >
            <FallbackChatUI />
          </Flex>
        ) : (
          <Flex justify="space-between" vertical>
            <div>
              <Layout.Header style={{ background: colors.primaryColor }}>
                {selectedUserDetails &&
                  selectedUserDetails !== null &&
                  selectedUserDetails !== undefined &&
                  Object.keys(selectedUserDetails)?.length !== 0 && (
                    <Flex align="center" style={{ height: "100%" }} gap="1rem">
                      {backButtonDetails?.isBackButton === true && (
                        <Button
                          type="default"
                          size="small"
                          icon={<LeftOutlined />}
                          onClick={() => navigate(backButtonDetails?.navigate)}
                        >
                          {t("CommonUtils.back")}
                        </Button>
                      )}
                      <Avatar
                        size={50}
                        src={
                          selectedUserDetails?.to_id?.role ===
                          userRoles.CUSTOMER
                            ? selectedUserDetails?.to_id?.profile_image
                            : selectedUserDetails?.to_id?.role ===
                                userRoles.SUPER_ADMIN &&
                              userSession.userRole === userRoles.SUPER_ADMIN
                            ? selectedUserDetails?.organization_id
                                ?.logo_image !== "" &&
                              selectedUserDetails?.organization_id
                                ?.logo_image !== null &&
                              selectedUserDetails?.organization_id
                                ?.logo_image !== undefined
                              ? selectedUserDetails?.organization_id?.logo_image
                              : selectedUserDetails?.to_id?.profile_image
                            : appAssetsConfig.DEFAULT_PROFILE_IMAGE
                        }
                        icon={<UserOutlined />}
                      />
                      <Typography.Paragraph
                        level={3}
                        style={{ color: colors.white, margin: 0 }}
                      >
                        {" "}
                        {`${
                          selectedUserDetails?.to_id?.role == userRoles.CUSTOMER
                            ? selectedUserDetails?.to_id?.first_name +
                              " " +
                              selectedUserDetails?.to_id?.last_name
                            : selectedUserDetails?.to_id?.role ===
                                userRoles.SUPER_ADMIN &&
                              userSession.userRole === userRoles.SUPER_ADMIN
                            ? selectedUserDetails?.organization_id?.name
                            : selectedUserDetails?.to_id?.first_name +
                              " " +
                              selectedUserDetails?.to_id?.last_name
                        } `}
                      </Typography.Paragraph>
                    </Flex>
                  )}
              </Layout.Header>
              <div ref={messageContainerRef} className="messageContainer">
                {loading ? (
                  <Spinner />
                ) : (
                  chatMessages?.map((element) => (
                    <>
                      {element?.message_type === "TEXT" &&
                        element?.images?.length !== 0 &&
                        element?.images?.map((elem) => {
                          return (
                            <MessageBox
                              onDownload={() => {
                                const url = elem?.url;
                                if (
                                  url &&
                                  url !== null &&
                                  url !== undefined &&
                                  url !== ""
                                ) {
                                  downloadUrl(url);
                                }
                              }}
                              notchStyle={{
                                fill: getMessagePosition(
                                  element,
                                  "backgroundColor"
                                ),
                              }}
                              position={getMessagePosition(element, "message")}
                              type={messageTypes["IMAGE"]}
                              data={{
                                uri: elem?.url,
                                width: 200,
                                height: "auto",
                                status: {
                                  click: false,
                                  loading: 0,
                                },
                              }}
                              title={elem?.name}
                              text={elem?.filename}
                              date={element?.created_at}
                              styles={{
                                backgroundColor: getMessagePosition(
                                  element,
                                  "backgroundColor"
                                ),
                              }}
                              ref={messageContainerRef}
                              className="message"
                            />
                          );
                        })}

                      {element?.message_type === "TEXT" &&
                        element?.documents?.length !== 0 &&
                        element?.documents?.map((elem) => {
                          return (
                            <MessageBox
                              onDownload={() => {
                                const url = elem?.url;
                                if (
                                  url &&
                                  url !== null &&
                                  url !== undefined &&
                                  url !== ""
                                ) {
                                  downloadUrl(url);
                                }
                              }}
                              notchStyle={{
                                fill: getMessagePosition(
                                  element,
                                  "backgroundColor"
                                ),
                              }}
                              position={getMessagePosition(element, "message")}
                              type={messageTypes["DOCUMENT"]}
                              data={{
                                uri: elem?.url,
                                width: "100%",
                                height: "auto",
                                status: {
                                  click: false,
                                  loading: 0,
                                },
                              }}
                              title={elem?.name}
                              text={elem?.filename}
                              date={element?.created_at}
                              styles={{
                                backgroundColor: getMessagePosition(
                                  element,
                                  "backgroundColor"
                                ),
                              }}
                              ref={messageContainerRef}
                              className="message"
                            />
                          );
                        })}

                      <MessageBox
                        onDownload={() => {
                          const url =
                            element?.message_type === "IMAGE"
                              ? element?.images[0]?.url
                              : element?.message_type === "DOCUMENT"
                              ? element?.documents[0]?.url
                              : "";
                          if (
                            url &&
                            url !== null &&
                            url !== undefined &&
                            url !== ""
                          ) {
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = "document.pdf";
                            link.target = "_blank";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        }}
                        notchStyle={{
                          fill: getMessagePosition(element, "backgroundColor"),
                        }}
                        position={getMessagePosition(element, "message")}
                        type={messageTypes[element?.message_type]}
                        data={{
                          uri:
                            element?.message_type === "IMAGE"
                              ? element?.images[0]?.url
                              : element?.message_type === "DOCUMENT"
                              ? element?.documents[0]?.url
                              : "",
                          width:
                            element?.message_type === "IMAGE" ? 200 : "100%",
                          height: "auto",
                          status: {
                            click: false,
                            loading: 0,
                          },
                        }}
                        title={element?.name}
                        text={
                          element?.message_type === "DOCUMENT"
                            ? element?.documents[0]?.filename
                            : element?.message
                        }
                        date={element?.created_at}
                        styles={{
                          backgroundColor: getMessagePosition(
                            element,
                            "backgroundColor"
                          ),
                        }}
                        ref={messageContainerRef}
                        className="message"
                      />
                    </>
                  ))
                )}
                {requestLoader ? (
                  <Spinner />
                ) : (
                  open &&
                  requestMessage &&
                  requestMessage?.response &&
                  requestMessage?.user && (
                    <div className={`dialog ${open ? "open" : ""}`}>
                      <Flex
                        vertical
                        justify="space-between"
                        gap="1rem"
                        style={{ margin: 20 }}
                      >
                        <Row gutter={[16, 16]}>
                          <Col span={6}>
                            <Avatar
                              size={60}
                              shape="square"
                              src={requestMessage?.user?.avatar}
                              icon={<UserOutlined />}
                            />
                          </Col>
                          <Col span={18}>
                            <Flex vertical style={{ textAlign: "left" }}>
                              <Typography.Title
                                level={3}
                                color="secondary"
                                style={{ margin: 0 }}
                              >
                                {requestMessage?.user?.title}
                              </Typography.Title>

                              <Typography.Title
                                level={5}
                                color="secondary"
                                style={{ margin: 0 }}
                              >
                                <span style={{ color: colors.slateGray }}>
                                  {t("CommonUtils.category")} :
                                </span>{" "}
                                {
                                  requestMessage?.response?.chat_post_id
                                    ?.category?.name
                                }
                              </Typography.Title>

                              <Flex gap="0.5rem">
                                <Typography.Title
                                  level={5}
                                  color="secondary"
                                  style={{
                                    margin: 0,
                                    color: colors.lightGreen,
                                  }}
                                >
                                  {" "}
                                  <span style={{ color: colors.slateGray }}>
                                    {t("CommonUtils.subCategory")} :
                                  </span>{" "}
                                  {requestMessage?.response?.chat_post_id?.subcategories
                                    ?.map((element) => element?.name)
                                    .join(", ")}
                                </Typography.Title>
                              </Flex>
                            </Flex>
                          </Col>
                        </Row>

                        <div style={{ height: 120, overflowY: "auto" }}>
                          {(requestMessage?.response?.chat_post_id?.images
                            ?.length !== 0 ||
                            requestMessage?.response?.chat_post_id?.documents
                              ?.length !== 0) && (
                            <>
                              <Flex vertical align="center" justify="center">
                                <Upload
                                  listType="picture-card"
                                  fileList={[
                                    ...requestMessage?.response?.chat_post_id
                                      ?.images,
                                    ...requestMessage?.response?.chat_post_id
                                      ?.documents,
                                  ]}
                                  accept="image/*, application/pdf"
                                  onPreview={handleAttachmentPreview}
                                  className="uploadContainer"
                                ></Upload>
                                <Modal
                                  open={openPreviewDialog}
                                  title={t("Modules.Appointments.previewTitle")}
                                  footer={null}
                                  onCancel={() => setOpenPreviewDialog(false)}
                                >
                                  <img
                                    style={{ width: "100%" }}
                                    src={previewImageSrc}
                                  />
                                </Modal>
                              </Flex>
                            </>
                          )}
                          {requestMessage?.response?.chat_post_id?.videos
                            ?.length !== 0 && (
                            <Flex align="center" justify="center">
                              {requestMessage?.response?.chat_post_id?.videos?.map(
                                (video) => {
                                  return (
                                    <div
                                      style={{
                                        maxWidth: "100%",
                                        border: "1px solid lightGray",
                                        padding: "5px",
                                        borderRadius: "10px",
                                      }}
                                    >
                                      <video width={200} controls>
                                        <source src={video.url} />
                                        Your browser does not support HTML5
                                        video.
                                      </video>
                                    </div>
                                  );
                                }
                              )}
                            </Flex>
                          )}
                        </div>

                        <Flex>
                          <Typography.Paragraph>
                            {requestMessage?.response?.chat_post_id?.message ||
                              ""}
                          </Typography.Paragraph>
                        </Flex>
                        <Flex gap="1rem" align="center" justify="center">
                          <Button
                            type="primary"
                            style={{ background: colors.lightGreen }}
                            onClick={() =>
                              onRequestStatusChanged(
                                requestMessage?.response?._id,
                                "approved",
                                setRequestLoader,
                                setOpen
                              )
                            }
                          >
                            {t("CommonUtils.accept")}
                          </Button>
                          <Button
                            type="primary"
                            danger
                            onClick={() =>
                              onRequestStatusChanged(
                                requestMessage?.response?._id,
                                "rejected",
                                setRequestLoader
                              )
                            }
                          >
                            {t("CommonUtils.reject")}
                          </Button>
                        </Flex>
                      </Flex>
                    </div>
                  )
                )}
              </div>
            </div>
            {chatMessages &&
            chatMessages !== null &&
            chatMessages !== undefined ? (
              <Form
                form={form}
                layout="vertical"
                autoComplete="off"
                requiredMark={false}
                onFinish={onSendMessage}
              >
                <Row
                  gutter={[16, 16]}
                  style={{
                    padding: 10,
                    background: colors.white,
                    textAlign: "center",
                  }}
                  justify="center"
                >
                  <Col span={1} style={{ margin: "auto" }}>
                    <Popover
                      content={
                        <EmojiPicker
                          handleGetEmoji={(emoji) => {
                            const message = form.getFieldValue("message");
                            form.setFieldValue(
                              "message",
                              (message || "") + emoji
                            );
                            inputRef.current.focus();
                          }}
                        />
                      }
                      trigger="click"
                    >
                      <Button
                        type="dashed"
                        size="small"
                        shape="circle"
                        disabled={disabledChat}
                        icon={<SmileOutlined />}
                        onClick={() => {
                          inputRef.current.focus();
                          setOpenEmojiPickerDialog(true);
                        }}
                      />
                    </Popover>
                  </Col>
                  <Col span={1} style={{ margin: "auto" }}>
                    <Popover
                      content={
                        <Flex gap="1rem">
                          <Tooltip
                            placement="leftTop"
                            title="Image"
                            color={colors.DarkMint}
                          >
                            <Button
                              type="dashed"
                              size="middle"
                              shape="circle"
                              title="Image"
                              style={{
                                background: "#9A37A3",
                                color: colors.white,
                              }}
                              disabled={disabledChat}
                              icon={
                                <FileImageOutlined style={{ fontSize: 20 }} />
                              }
                              onClick={() => imageRef.current?.click()}
                            />
                          </Tooltip>
                          <Tooltip
                            placement="rightTop"
                            title="Document"
                            color={colors.DarkMint}
                          >
                            <Button
                              type="dashed"
                              size="middle"
                              shape="circle"
                              title="Document"
                              style={{
                                background: "#5F66CD",
                                color: colors.white,
                              }}
                              disabled={disabledChat}
                              icon={<FolderOutlined style={{ fontSize: 20 }} />}
                              onClick={() => documentRef.current?.click()}
                            />
                          </Tooltip>
                          <input
                            type="file"
                            style={{ display: "none" }}
                            ref={imageRef}
                            accept="image/*"
                            onChange={onImageChanged}
                          />
                          <input
                            type="file"
                            style={{ display: "none" }}
                            ref={documentRef}
                            accept=".pdf, .xls, .xlsx"
                            onChange={onDocumentChanged}
                          />
                        </Flex>
                      }
                      trigger="click"
                    >
                      <Button
                        type="dashed"
                        size="small"
                        shape="circle"
                        disabled={disabledChat}
                        icon={<PaperClipOutlined />}
                      />
                    </Popover>
                  </Col>

                  <Col span={20} style={{ margin: "auto" }}>
                    <Form.Item name="message" style={{ margin: "auto" }}>
                      <Input
                        style={{ width: "100%" }}
                        placeholder="Type here...."
                        ref={inputRef}
                        disabled={disabledChat}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={2} style={{ margin: "auto" }}>
                    <Button
                      type="dashed"
                      htmlType="submit"
                      size="middle"
                      icon={<SendOutlined />}
                      disabled={disabledChat}
                    />
                  </Col>
                </Row>
              </Form>
            ) : (
              <></>
            )}
          </Flex>
        )}
      </Col>
    </Row>
  );
};

export default ChatComponent;
